import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Beer,
  Check,
  Layers,
  MessageSquare,
  Package2,
  Cookie,
  Sparkles,
  User,
} from 'lucide-react';
import {
  fetchSuppliersWithSection,
  searchProductsBySection,
  sectionOf,
  SECTION_LABEL,
  updatePackage,
  updateProject,
  updateProjectClient,
} from '@/lib/api';
import type { PackageSection } from '@/lib/api';
import type { Client, GiftPackage, GiftProduct, GiftSupplier, Project } from '@/lib/types';
import { calculateGiftPackagePricing } from '@/lib/packagePricing';
import { ClientPicker } from './ClientPicker';
import { CloneStep } from './CloneStep';
import { ConversationStep } from './ConversationStep';

interface Props {
  pkg: GiftPackage;
  project?: Project;
  client?: Client | null;
  clientName?: string | null;
  onAdd: (product: GiftProduct) => void | Promise<void>;
  onRemove: (itemId: string) => void | Promise<void>;
  onFinish: () => void;
  onProjectChanged?: () => void;
  initialStepId?: StepId;
}

type StepId = PackageSection | 'gesprek' | 'basis' | 'kloon';

interface Step {
  id: StepId;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  optional: boolean;
}

const STEPS: Step[] = [
  {
    id: 'basis',
    title: 'Klant + basis',
    description: 'Klant, projectnaam, aantal pakketten, budget en deadline.',
    icon: User,
    optional: false,
  },
  {
    id: 'gesprek',
    title: 'Gesprek',
    description: 'Plak het transcript of upload de opname. AI maakt een samenvatting + tips.',
    icon: MessageSquare,
    optional: true,
  },
  {
    id: 'kloon',
    title: 'Kopiëren',
    description: 'Start vanaf een eerder pakket en kies de best passende variant.',
    icon: Layers,
    optional: true,
  },
  {
    id: 'verpakking',
    title: 'Verpakking',
    description: 'Kies een doos voor de pakketten. DS Smith of Baka.',
    icon: Package2,
    optional: true,
  },
  {
    id: 'gadget',
    title: 'Gadgets',
    description: 'Selecteer 1 of meer gadgets.',
    icon: Sparkles,
    optional: true,
  },
  {
    id: 'drank',
    title: 'Drank',
    description: 'Speciaalbier of andere drank toevoegen.',
    icon: Beer,
    optional: true,
  },
  {
    id: 'food',
    title: 'Food',
    description: 'Vul het pakket met food binnen het resterende budget.',
    icon: Cookie,
    optional: true,
  },
  {
    id: 'overig',
    title: 'Klaar',
    description: 'Bekijk je pakket. Switch naar Cockpit om vrij te tweaken.',
    icon: Check,
    optional: false,
  },
];

/** Intro-stappen (geen inhoud-sectie): niet genummerd in de stepper. */
function isMetaStep(id: StepId): boolean {
  return id === 'basis' || id === 'gesprek' || id === 'kloon';
}

export function WizardBuilder({
  pkg,
  project,
  client,
  clientName,
  onAdd,
  onRemove,
  onFinish,
  onProjectChanged,
  initialStepId,
}: Props) {
  const [stepIdx, setStepIdx] = useState(() => {
    if (initialStepId) {
      const i = STEPS.findIndex((s) => s.id === initialStepId);
      return i >= 0 ? i : 0;
    }
    return 0;
  });
  const step = STEPS[stepIdx];
  const isFinalStep = stepIdx === STEPS.length - 1;
  const isConversationStep = step.id === 'gesprek';
  const isBasisStep = step.id === 'basis';
  const isCloneStep = step.id === 'kloon';

  const itemsBySection = useMemo(() => {
    const map = new Map<PackageSection, GiftPackage['items']>();
    for (const s of STEPS) {
      if (!isMetaStep(s.id)) map.set(s.id as PackageSection, []);
    }
    for (const item of pkg.items ?? []) {
      if (!item.gift_product) continue;
      const sec = sectionOf(item.gift_product);
      map.set(sec, [...(map.get(sec) ?? []), item]);
    }
    return map;
  }, [pkg.items]);

  return (
    <div className="flex h-full flex-col gap-4">
      <Stepper currentIdx={stepIdx} onJump={setStepIdx} itemsBySection={itemsBySection} />

      <div className="flex-1 overflow-auto rounded-xl border border-bmade-200 bg-white p-6 dark:border-bmade-800 dark:bg-bmade-900/40">
        {isBasisStep && project ? (
          <BasisStep
            project={project}
            pkg={pkg}
            client={client ?? null}
            onChanged={() => onProjectChanged?.()}
          />
        ) : isConversationStep && project ? (
          <ConversationStep
            project={project}
            clientId={pkg.client_id ?? project.client_id}
            clientName={clientName}
            packageBudget={pkg.budget_per_package}
            packageQuantity={pkg.quantity}
            budgetType={pkg.budget_type}
            onSaved={() => onProjectChanged?.()}
          />
        ) : isCloneStep ? (
          <CloneStep
            pkg={pkg}
            onCloned={() => {
              onProjectChanged?.();
              setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
            }}
          />
        ) : isFinalStep ? (
          <FinalStep
            pkg={pkg}
            itemsBySection={itemsBySection}
            onRemove={onRemove}
            onFinish={onFinish}
          />
        ) : (
          <StepCatalog
            step={step}
            pkg={pkg}
            currentItems={itemsBySection.get(step.id as PackageSection as PackageSection) ?? []}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
          disabled={stepIdx === 0}
          className="inline-flex items-center gap-2 rounded-md border border-bmade-200 px-3 py-2 text-sm text-bmade-700 transition-colors hover:border-bmade-400 hover:bg-bmade-100 disabled:opacity-30 dark:border-bmade-700 dark:text-bmade-200 dark:hover:border-bmade-500 dark:hover:bg-bmade-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Terug
        </button>

        <div className="flex items-center gap-2">
          {!isFinalStep && step.optional && (
            <button
              type="button"
              onClick={() => setStepIdx(stepIdx + 1)}
              className="text-xs uppercase tracking-wider text-bmade-500 transition-colors hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white"
            >
              {(step.id as PackageSection) === 'verpakking'
                ? 'Geen verpakking nodig — skip'
                : `Skip ${step.title.toLowerCase()}`}
            </button>
          )}
          {isFinalStep ? (
            <button
              type="button"
              onClick={onFinish}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
            >
              Naar cockpit
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStepIdx(stepIdx + 1)}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
            >
              Verder
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({
  currentIdx,
  onJump,
  itemsBySection,
}: {
  currentIdx: number;
  onJump: (idx: number) => void;
  itemsBySection: Map<PackageSection, GiftPackage['items']>;
}) {
  // De stepper toont de volledige flow genummerd: eerst de intro-stappen
  // (Klant, Gesprek, Kopiëren), daarna de inhoud-secties. Zo zie je ook tijdens
  // de intro waar je zit in het geheel.
  const allSteps = STEPS.map((step, idx) => ({ step, idx }));
  return (
    <ol className="flex items-center gap-1 rounded-xl border border-bmade-200 bg-white p-2 text-xs dark:border-bmade-800 dark:bg-bmade-900/40">
      {allSteps.map(({ step, idx }, pos) => {
        const Icon = step.icon;
        const count = isMetaStep(step.id)
          ? 0
          : (itemsBySection.get(step.id as PackageSection)?.length ?? 0);
        const isActive = idx === currentIdx;
        const isPast = idx < currentIdx;
        return (
          <li key={step.id as PackageSection} className="flex flex-1 items-center gap-1">
            <button
              type="button"
              onClick={() => onJump(idx)}
              className={`flex flex-1 items-center gap-2 rounded-md px-3 py-2 text-left transition-colors ${
                isActive
                  ? 'bg-accent-500/15 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                  : isPast
                    ? 'text-bmade-700 hover:bg-bmade-100 dark:text-bmade-300 dark:hover:bg-bmade-800/60'
                    : 'text-bmade-500 hover:bg-bmade-50 dark:text-bmade-500 dark:hover:bg-bmade-900'
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${
                  isActive
                    ? 'bg-accent-500 text-white'
                    : isPast
                      ? 'bg-bmade-200 text-bmade-700 dark:bg-bmade-700 dark:text-bmade-200'
                      : 'border border-bmade-300 text-bmade-500 dark:border-bmade-700 dark:text-bmade-500'
                }`}
              >
                {isPast ? <Check className="h-3 w-3" /> : pos + 1}
              </span>
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="flex items-center gap-1 text-sm font-medium">
                  <Icon className="h-3 w-3" />
                  {step.title}
                </span>
                {count > 0 && (
                  <span className="text-[10px] text-bmade-500">
                    {count} {count === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
            </button>
            {pos < allSteps.length - 1 && (
              <span className="text-bmade-300 dark:text-bmade-700">›</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function StepCatalog({
  step,
  pkg,
  currentItems,
  onAdd,
  onRemove,
}: {
  step: Step;
  pkg: GiftPackage;
  currentItems: NonNullable<GiftPackage['items']>;
  onAdd: (product: GiftProduct) => void | Promise<void>;
  onRemove: (id: string) => void | Promise<void>;
}) {
  const [suppliers, setSuppliers] = useState<GiftSupplier[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<GiftProduct[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSuppliersWithSection(step.id as PackageSection)
      .then((list) => {
        setSuppliers(list);
        if (supplierId && !list.some((s) => s.id === supplierId)) {
          setSupplierId('');
        }
      })
      .catch(() => setSuppliers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id as PackageSection]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      searchProductsBySection(step.id as PackageSection, query, supplierId || undefined, 24)
        .then(setProducts)
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [step.id as PackageSection, query, supplierId]);

  const selectedIds = new Set(currentItems.map((it) => it.gift_product_id));

  return (
    <div className="flex h-full flex-col gap-4">
      <header>
        <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
          Stap voor pakket van {pkg.quantity} stuks
        </div>
        <h2 className="mt-1 text-xl font-semibold text-bmade-950 dark:text-white">
          {SECTION_LABEL[step.id as PackageSection]}
        </h2>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">{step.description}</p>
      </header>

      {currentItems.length > 0 && (
        <div className="rounded-md border border-bmade-200 bg-bmade-50/60 p-3 dark:border-bmade-800 dark:bg-bmade-950/40">
          <div className="mb-2 text-[11px] uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
            Al gekozen
          </div>
          <ul className="flex flex-wrap gap-2">
            {currentItems.map((it) => {
              const p = it.gift_product;
              if (!p) return null;
              return (
                <li
                  key={it.id}
                  className="inline-flex items-center gap-2 rounded-full border border-bmade-300 bg-white px-3 py-1 text-xs dark:border-bmade-700 dark:bg-bmade-900"
                >
                  <span className="text-bmade-900 dark:text-white">{p.name}</span>
                  <button
                    type="button"
                    onClick={() => onRemove(it.id)}
                    className="text-bmade-500 hover:text-red-500"
                    title="Verwijderen"
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          className="rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
        >
          <option value="">Alle leveranciers</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek naam of artikelnr..."
          className="rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400"
        />
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <p className="text-sm text-bmade-500">Zoeken...</p>
        ) : products.length === 0 ? (
          <EmptyHint section={step.id as PackageSection} />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                selected={selectedIds.has(p.id)}
                onAdd={() => onAdd(p)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyHint({ section }: { section: PackageSection }) {
  if (section === 'verpakking') {
    return (
      <p className="text-sm text-bmade-500">
        Nog geen verpakking in de catalogus. DS Smith en Baka moeten geïmporteerd worden voor deze
        stap data heeft. Klik onderaan "skip" om door te gaan zonder verpakking.
      </p>
    );
  }
  return <p className="text-sm text-bmade-500">Geen resultaten voor deze filter.</p>;
}

function ProductCard({
  product,
  selected,
  onAdd,
}: {
  product: GiftProduct;
  selected: boolean;
  onAdd: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={selected}
      className={`group flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition-all ${
        selected
          ? 'cursor-not-allowed border-accent-500 bg-accent-500/5 dark:bg-accent-500/10'
          : 'border-bmade-200 bg-white hover:border-accent-400 hover:shadow-sm dark:border-bmade-800 dark:bg-bmade-900/40 dark:hover:border-accent-400'
      }`}
    >
      <div className="flex h-24 w-full items-center justify-center rounded-md bg-bmade-100 text-xs text-bmade-500 dark:bg-bmade-800 dark:text-bmade-400">
        {product.brand?.slice(0, 16) ?? product.supplier?.name?.slice(0, 16) ?? 'Product'}
      </div>
      <div className="w-full">
        <div className="line-clamp-2 text-sm font-medium text-bmade-950 dark:text-white">
          {product.name}
        </div>
        <div className="mt-1 text-[11px] text-bmade-500">
          {product.supplier?.name ?? '—'}
          {product.brand && ` · ${product.brand}`}
        </div>
        <div className="mt-1 flex items-center justify-between text-xs">
          <span className="font-semibold text-bmade-950 dark:text-white">
            EUR {product.unit_purchase_price.toFixed(2)}
          </span>
          <span className="text-bmade-500">
            colli {product.vpe} · {product.btw_category === 'low' ? '9%' : '21%'}
          </span>
        </div>
      </div>
      <div
        className={`mt-1 w-full rounded-md py-1 text-center text-xs font-medium transition-colors ${
          selected
            ? 'bg-accent-500/15 text-accent-700 dark:text-accent-300'
            : 'bg-bmade-100 text-bmade-700 group-hover:bg-accent-500 group-hover:text-white dark:bg-bmade-800 dark:text-bmade-200'
        }`}
      >
        {selected ? '✓ Toegevoegd' : 'Selecteer'}
      </div>
    </button>
  );
}

function FinalStep({
  pkg,
  itemsBySection,
  onRemove,
  onFinish,
}: {
  pkg: GiftPackage;
  itemsBySection: Map<PackageSection, GiftPackage['items']>;
  onRemove: (id: string) => void | Promise<void>;
  onFinish: () => void;
}) {
  const pricing = calculateGiftPackagePricing(pkg);
  const sectionsWithItems = (
    ['verpakking', 'gadget', 'drank', 'food', 'overig'] as PackageSection[]
  ).filter((sec) => (itemsBySection.get(sec)?.length ?? 0) > 0);

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
          Pakket-overzicht
        </div>
        <h2 className="mt-1 text-2xl font-semibold text-bmade-950 dark:text-white">{pkg.name}</h2>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          {pkg.quantity} pakketten, brutomarge {pricing.total.weighted_margin_pct.toFixed(1)}%.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          label="Inkoop"
          value={`EUR ${(
            pricing.components.low.cost + pricing.components.high.cost
          ).toFixed(2)}`}
        />
        <SummaryCard
          label="Handling + transport"
          value={`EUR ${(
            pricing.components.handling.cost + pricing.components.transport.cost
          ).toFixed(2)}`}
        />
        <SummaryCard
          label="Verkoop ex btw"
          value={`EUR ${pricing.total.selling_excl_btw.toFixed(2)}`}
          highlight
        />
      </div>

      {sectionsWithItems.length === 0 ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
          Nog geen items toegevoegd. Ga terug en kies producten in de eerdere stappen.
        </p>
      ) : (
        <div className="space-y-3">
          {sectionsWithItems.map((sec) => (
            <section key={sec} className="rounded-md border border-bmade-200 dark:border-bmade-800">
              <header className="flex items-center justify-between border-b border-bmade-200 px-3 py-2 dark:border-bmade-800">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-bmade-700 dark:text-bmade-300">
                  {SECTION_LABEL[sec]}
                </h3>
                <span className="text-[11px] text-bmade-500">
                  {itemsBySection.get(sec)?.length} items
                </span>
              </header>
              <ul className="divide-y divide-bmade-200 dark:divide-bmade-800">
                {(itemsBySection.get(sec) ?? []).map((it) => {
                  const p = it.gift_product;
                  if (!p) return null;
                  return (
                    <li
                      key={it.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-bmade-950 dark:text-white">
                          {p.name}
                        </div>
                        <div className="text-bmade-500">
                          {p.supplier?.name ?? '—'} · EUR {p.unit_purchase_price.toFixed(2)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemove(it.id)}
                        className="text-bmade-500 hover:text-red-500"
                      >
                        Verwijder
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-bmade-200 bg-bmade-50/60 p-4 dark:border-bmade-800 dark:bg-bmade-950/40">
        <Layers className="mb-2 h-5 w-5 text-bmade-500" />
        <p className="text-sm text-bmade-700 dark:text-bmade-300">
          Klaar voor tweaks. In de cockpit kun je losse items aanpassen, vergelijken met
          klant-historie en de marge fijn-tunen.
        </p>
        <button
          type="button"
          onClick={onFinish}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600"
        >
          Naar cockpit
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlight
          ? 'border-accent-300 bg-accent-50 dark:border-accent-500/40 dark:bg-accent-500/10'
          : 'border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40'
      }`}
    >
      <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-bmade-950 dark:text-white">{value}</div>
    </div>
  );
}

function BasisStep({
  project,
  pkg,
  client,
  onChanged,
}: {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
  onChanged: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [deadline, setDeadline] = useState(project.deadline ?? '');
  const [quantity, setQuantity] = useState(pkg.quantity);
  const [budget, setBudget] = useState(pkg.budget_per_package);
  const [budgetType, setBudgetType] = useState<'incl_btw' | 'excl_btw'>(pkg.budget_type);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(project.name);
    setDeadline(project.deadline ?? '');
  }, [project.id, project.name, project.deadline]);

  useEffect(() => {
    setQuantity(pkg.quantity);
    setBudget(pkg.budget_per_package);
    setBudgetType(pkg.budget_type);
  }, [pkg.id, pkg.quantity, pkg.budget_per_package, pkg.budget_type]);

  async function saveProject(patch: Partial<Project>, field: string) {
    setSavingField(field);
    setError(null);
    try {
      await updateProject(project.id, patch);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSavingField(null);
    }
  }

  async function savePackage(patch: Partial<GiftPackage>, field: string) {
    setSavingField(field);
    setError(null);
    try {
      await updatePackage(pkg.id, patch);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSavingField(null);
    }
  }

  async function handleClientChange(c: Client | null) {
    setSavingField('client');
    setError(null);
    try {
      await updateProjectClient(project.id, c?.id ?? null);
      await updatePackage(pkg.id, { client_id: c?.id ?? null });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Klant koppelen mislukt');
    } finally {
      setSavingField(null);
    }
  }

  const inputCls =
    'w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white';
  const labelCls =
    'mb-1 flex items-center justify-between text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400';

  const clientValue: Client | null = client ?? null;

  return (
    <div className="flex h-full flex-col gap-5">
      <header>
        <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
          Stap 1: Klant + basis
        </div>
        <h2 className="mt-1 text-xl font-semibold text-bmade-950 dark:text-white">
          Wijzig klant, naam, aantal of budget
        </h2>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          Wijzigingen worden direct opgeslagen. Gebruik 'Verder' om door te gaan naar het gesprek.
        </p>
      </header>

      <div className="space-y-5 rounded-2xl border border-bmade-200 bg-bmade-50/40 p-5 dark:border-bmade-800 dark:bg-bmade-950/30">
        <section>
          <div className={labelCls}>
            <span className="inline-flex items-center gap-1.5">
              <User className="h-3 w-3" /> Klant
            </span>
            {savingField === 'client' && <span className="text-bmade-500">opslaan...</span>}
          </div>
          <ClientPicker
            value={clientValue}
            onChange={(c) => void handleClientChange(c)}
            placeholder="Zoek klant..."
          />
        </section>

        <section>
          <div className={labelCls}>
            <span>Project-naam</span>
            {savingField === 'name' && <span className="text-bmade-500">opslaan...</span>}
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              if (name !== project.name) void saveProject({ name }, 'name');
            }}
            className={inputCls}
          />
        </section>

        <div className="grid grid-cols-2 gap-3">
          <section>
            <div className={labelCls}>
              <span>Aantal pakketten</span>
              {savingField === 'quantity' && <span className="text-bmade-500">opslaan...</span>}
            </div>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              onBlur={() => {
                if (quantity !== pkg.quantity) {
                  void savePackage({ quantity }, 'quantity');
                  void saveProject({ package_count_planned: quantity }, 'quantity');
                }
              }}
              className={inputCls}
            />
          </section>
          <section>
            <div className={labelCls}>
              <span>Deadline</span>
              {savingField === 'deadline' && <span className="text-bmade-500">opslaan...</span>}
            </div>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              onBlur={() => {
                if (deadline !== (project.deadline ?? '')) {
                  void saveProject({ deadline: deadline || null }, 'deadline');
                }
              }}
              className={inputCls}
            />
          </section>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <section>
            <div className={labelCls}>
              <span>Budget per pakket</span>
              {savingField === 'budget' && <span className="text-bmade-500">opslaan...</span>}
            </div>
            <input
              type="number"
              min={0}
              step={0.5}
              value={budget}
              onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
              onBlur={() => {
                if (budget !== pkg.budget_per_package) {
                  void savePackage({ budget_per_package: budget }, 'budget');
                }
              }}
              className={inputCls}
            />
          </section>
          <section>
            <div className={labelCls}>
              <span>BTW</span>
              {savingField === 'budgetType' && <span className="text-bmade-500">opslaan...</span>}
            </div>
            <select
              value={budgetType}
              onChange={(e) => {
                const next = e.target.value as 'incl_btw' | 'excl_btw';
                setBudgetType(next);
                void savePackage({ budget_type: next }, 'budgetType');
              }}
              className={inputCls}
            >
              <option value="incl_btw">Incl. BTW</option>
              <option value="excl_btw">Excl. BTW</option>
            </select>
          </section>
        </div>

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
